import { CSSProperties, MouseEvent, MouseEventHandler } from "react";
import { DataTemplate } from "../map/sidesheet/addplace/AddSinglePlaceInput";
import { Buffer } from "buffer";
import { BASE_URL } from "../../data_provider/Config";
import { PG } from "../pricepolicy/IamPort";

// global variables ---------------------------------------------------------------
const IMP_CODE = "37294344"; // iamport 식별코드
const GET_FEATURE_UNIT = 20000; // 한 번에 호출하는 피처 단위 개수
const IMAGEKEY = "images";

// GA4 Measurement ID
//GA_MEASUREMENT_ID: Google Analytics 4 측정 ID
export const GA_MEASUREMENT_ID = "G-Q0V72VHF65";

/**
 * 공통 색상 글로벌 변수
 */
const enum Color {
    MAIN = "#0088FF",
    ACCENT = "#FF0066",
    BASIC_MARKER = "#BA2022", // 기본 마커용 색상
}

/**
 * 아이콘 사이즈 프리셋
 */
const iconSize: { [key: string]: string } = {
    xxsmall: "10px",
    xsmall: "16px",
    small: "18px",
    medium: "20px",
    default: "24px",
    large: "32px",
    xlarge: "40px",
};

const path = {
    ROOT: "/",
    CREATE_MAP: "/create",
    GUIDE: "/features",
    PRICE_POLICY: "/price",
    SUPPORT: "/inquire",
    PUBLIC_MAPLIST: "/public",
    CONTACT: "/contact",
    COMPANY_INFO: "/company",
    MAP: "/map",
    MOBILE_MAP: "/m/map",
    MYPAGE: "/mypage",
    TERMS: "/terms",
    STATISTICS: "/statistics",
    SIGNUP: "/signup",
    SIGNUP_ADD_INFO: "/signup-add-info",
    FIND_ACCOUNT: "/find-account",
    PASSWORD_CHANGE: "/password-change",
    PORTAL_LOGIN_KAKAO: "/login/kakao",
    PORTAL_LOGIN_NAVER: "/login/naver",
    PORTAL_LOGIN_GOOGLE: "/login/google",
    PORTAL_LOGIN_FACEBOOK: "/login/facebook",
    PRIVACY: "/privacy"
} as const;

/**
 * sub 페이지 경로
 */
const subPaths: { [key: string]: string[] } = {
    MYPAGE: ["user", "purchase", "mymap"],
};

/**
 * @author L.chanwoo
 * @remark 마커컬러 팔레트
 */
const markerColors: string[] = [
    "#ba2022",
    "#d54914",
    "#ffd500",
    "#017439",
    "#0066a1",
    "#352a54",
    "#653179",
    "#851a4e",
    "#f9107b",
    "#ff7b58",
    "#cc9933",
    "#8ab532",
    "#109496",
    "#687cae",
    "#9865ce",
    "#cc6092",
];

/**
 * 유료 플랜별 가격
 */
const GRADE: { [key: string]: gradeInfo } = {
    normal: {
        name: "일반사용자",
    },
    pro: {
        name: "모두의지도 PRO",
        price: 20000,
    },
    admin: {
        name: "관리자",
    },
} as const;

/**
 * 아이콘 색상 필터 프리셋
 * @author bakim
 * @description maskImage로 사용하며 프리셋 세팅 변경
 */
const iconColor: { [key: string]: string } = {
    default: "var(--main-color)",
    red: "var(--styles-color-del)",
    darkGrey: "var(--styles-color-undo)",
    buttonDarkGrey: "var(--button-dark-grey)",
    grey: "var(--text-grey)",
    lightGrey: "var(--light-grey)",
    yellow: "var(--styles-color-yellow)",
    white: "white",
    none: "black",
};

// interfaces ---------------------------------------------------------------------
/**
 * 리스트뷰 테이블 스키마 ( ListViewHeader 와 ListViewItem 컴포넌트의 prop 으로 사용됨 )
 * @author khy
 * @date 2022-09-01 10:08
 * @prop field 컬럼 key. data에서의 각 요소별 key 이름
 * @prop title ListViewHeader 에 표현될 컬럼명
 * @prop expression (optional) 해당 컬럼을 특정 string 으로 표현하고 싶은 경우 사용 ex) 해당 컬럼의 data 들의 값을 💾 으로 표현
 * @prop widthRatio (optional) 해당 컬럼이 배정받는 넓이에 대한 비율을 지정 (모든 컬럼 widthRatio 를 이용해 100% 대비 width 가 계산되어 각 컬럼의 넓이가 계산됨)
 * @prop style (optional) 해당 컬럼에 CSS style 을 지정하고 싶은 경우 사용
 * @prop onClick (optional) 해당 컬럼에 onClick Event 를 주고 싶은 경우 사용
 */
interface ListViewSchema {
    field: string;
    title: string;
    expression?: string;
    widthRatio?: number;
    style?: CSSProperties;
    onClick?: (event: any, data: any) => void;
}

interface MapCount {
    today: number;
    total: number;
}

/**
 * 카테고리 버튼 설정
 * @interface
 * @prop {string} color (optional) 카테고리 마커 색상
 * @prop {boolean} cancel (optional)
 * @prop {string} icon (optional) 마커 아이콘 이름
 * @prop {string} label (optional) 카테고리명
 * @prop {{@link MouseEventHandler}<{@link HTMLElement}>} onClick (optional) 카테고리 버튼 클릭 이벤트핸들러 함수
 * @prop {boolean} isActive (optional) 활성 상태 여부(false: default / true: 기능 사용중)
 * @prop {boolean} disabled (optional) 비활성화 여부(false: default / true: 클릭 불가능)
 */
interface CategoryOptions extends ButtonOptions {
    color?: string;
    cancel?: boolean;
    disabled?: boolean;
}

/**
 * 플로팅 버튼 기본 설정(카테고리/지도 플로팅 버튼에 사용)
 * @interface
 * @prop {string} icon (optional) 버튼 아이콘 이름
 * @prop {string} label (optional) 라벨
 * @prop {{@link MouseEventHandler}<{@link HTMLElement}>} onClick (optional) 버튼 클릭 이벤트핸들러 함수
 * @prop {boolean} isActive (optional) 활성 상태 여부(false: default / true: 기능 사용중)
 * @prop {boolean} disabled (optional) 비활성화 여부(false: default / true: 클릭 불가능)
 */
interface ButtonOptions {
    icon?: string;
    label?: string;
    onClick?: (...args: any) => void;
    isActive?: boolean;
    disabled?: boolean;
}

interface Category {
    icon: string;
    label: string;
    color: string;
}

/**
 * @author bakim
 * @date 2024-08-20 11:27:28
 * @remark 지도 별 사용자활동 정보 타입
 */
interface MapUserActivity {
    bookmark?: boolean;
    viewCnt: number;
    bookmarkCnt: number;
    thumbCnt: number;
}

interface BaseMapInfo {
    title: string;
    description?: string;
    keyword?: string[] | string;
    viewCnt: number;
    bookmarkCnt: number;
    thumbCnt: number;
}

/**
 * 지도 목록용 지도 정보
 */
interface MapItem extends BaseMapInfo {
    mapId: string;
    username: string;
    userId: string;
    createDate: string;
    images: string[];
    //mainImage?: string; //deprecated
    cacheDate?: string;
    private?: boolean;
}

/**
 * 지도 정보
 */
interface MapInfo extends BaseMapInfo {
    category: CategoryOptions[];
    bookmark: boolean;
    cacheDate?: string;
    updateDate?: string;
    famousMarker?: FamousMarker[];
    [key: string]: any;
}

interface FamousMarker {
    XID: number;
    url: string;
}

/**
 * @author bakim
 * @date 2024-08-22 10:31:43
 * @remark 메뉴 아이템
 * @param title 아이템 항목 이름(required)
 * @param onClickItem 클릭 콜백 함수(required)
 * @param hover
 */
interface MenusItem {
    title: string;
    onClickItem: (event: MouseEvent<HTMLElement>) => void;
    hover?: (event: MouseEvent<HTMLElement>) => void;
}

interface UserInfo {
    username: string;
    userId: string;
    emailNotification: number;
    admin?: boolean;
    membership?: Membership;
    platform?: number;
}

interface Membership {
    grade: string;
    gradeCode: number;
    expiredDate?: string;
    startDate?: string;
}

interface POIInfo {
    XID: number;
    placeName: string;
    address: string;
    viewCnt: number;
    storyCnt: number;
}

interface Story {
    SID: number;
    userId: string;
    userName: string;
    content: string;
    createDate: string;
    parentSid: number;
    like: boolean;
    likeCnt: number;
    reported: boolean;
    images: ImageInfo[];
    comments?: Story[];
    replies?: Story[];
    storyType?: string; // story, reply, comment
}
/**
 * @author bakim
 * @date 2024-10-08 13:35:47
 * @remark 이미지 정보
 * @param src 이미지 소스(required) 새로 추가한 이미지(image file) = File, 이미 저장된 이미지(url) = string
 * @param id 이미지 아이디 (신규 추가일 경우 id X)
 */
interface ImageInfo {
    src: File | string;
    id?: number;
}

// 신규 장소 추가용 데이터 타입 -----------------------------
interface PlaceProps {
    [fieldKey: string]: string;
}

interface PlacePropsList {
    [row: number]: PlaceProps;
}

interface PlaceCoords {
    coords: number[];
    category: string;
    label: string;
}

interface PlaceCoordsList {
    [row: number]: PlaceCoords;
}

interface FieldData {
    fieldKey: string;
    fieldName: string;
    template?: DataTemplate;
    style?: MainDataStyle;
}

interface ImageField extends FieldData {
    value: ImageInfo[];
}

interface Field extends FieldData {
    value: string;
}

/**
 *
 * @author bakim
 * @remark 맵 메타데이터
 */
interface MetaData {
    metadata: Metadata;
    template?: { [key: string]: DataTemplate };
    mapType: string;
}

interface Metadata {
    geoField: string;
    category?: string | null;
    marker?: string | null; // category가 null일 때 마커모양
    markerColor?: string | null; // category가 null일 때 마커색상
    placeName: string; // 장소 이름(라벨)
    imageField?: string | null;
    mainData: string[];
    extraData?: string[];
    fieldName: { [key: string]: string };
    XID?: string;
}

/**
 * 장소 정보
 */

interface PrimaryInfo {
    viewCnt: number;
    thumbCnt: number;
    // storyCnt: number;
}

interface PlaceInfoSet {
    primaryinfo: PrimaryInfo;
    placeInfo: Record<string, any>;
}

type AllPlaceData = Record<number, PlaceInfoSet>;

interface PlaceData {
    XID?: number;
    address: Field;
    placeName: Field;
    category: Field;
    mainData: Field[];
    imageField?: ImageField;
    extraData?: Field[];
}

/**
 * 장소 주요 정보 스타일
 */
interface MainDataStyle {
    color?: string;
    fontStyle?: string;
    fontWeight?: string;
    textDecoration?: string;
    textAlign?: string;
}

/**
 * 멤버십 등급 종류
 * gradeCode: admin 0, normal 1, pro 2
 */
type GradeType = "admin" | "normal" | "pro";

/**
 * 멤버십 등급 정보
 */
interface gradeInfo {
    name: string;
    price?: number;
}

/**
 * 결제 요청시 필수 데이터
 */
interface PaymentData {
    name: string;
    email: string;
    pg?: PG;
    upgradeType?: GradeType;
}

// common functions -------------------------------------------------------
/**
 * 아이디(fullEmail) 유효성 검사
 */
function validateFullEmail(id: string) {
    // const regex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const regex = /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/i;
    return regex.test(id);
}

/**
 * 이메일(ID부분) 유효성 검사
 */
function validateEmailId(id: string) {
    // 이메일 ID 4~30자, 알파벳, 숫자, 일부특수문자._-만 허용(공백 제외)
    const regex = /^[a-zA-Z0-9._-]{4,30}$/;
    return regex.test(id);
}

/**
 * 이메일(도메인) 유효성 검사
 */
function validateDomain(domain: string) {
    const regex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(domain);
}

/**
 * 비밀번호 유효성 검사
 */
const validatePassword = (password: string) => {
    const regex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,16}$/;
    return regex.test(password);
};
/**
 * 이름 유효성 검사
 */
function validateName(name: string) {
    // 이름 길이: 2~20자, 한글, 알파벳만 허용(공백 제외)
    const regex = /^[A-Za-z가-힣]{2,20}$/;
    return regex.test(name);
}

// gsshin (26.1.13) 파라메터 타입 예외 처리
const getFormattedNumberStr = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === "") return "";
    const str = typeof value === "number" ? value.toString() : value;
    return str.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 * csv string -> 파일 다운로드 함수
 * @author mjkang
 * @param csv string 포맷의 csv
 * @param fileName 저장될 파일명
 */
const downloadCSV = (csv: string, fileName: string) => {
    const hiddenElem = document.createElement("a");

    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    hiddenElem.href = url;
    hiddenElem.target = "_blank";
    hiddenElem.download = `${fileName}.csv`;
    hiddenElem.click();
};

/**
 * jsonArray -> csv string 변환 함수
 * @author mjkang
 * @param jsonArray csv로 변환할 jsonarray 데이터 (object 배열)
 * @returns csv string
 */
const JSONArrayToCSV = (jsonArray: PlaceProps[]) => {
    const keys = Object.keys(jsonArray[0]); // 첫 번째 열: fieldName
    const result = jsonArray
        .map((json) => {
            const row = keys.map((key) => json[key]).join(",");
            return row;
        })
        .join("\r\n");

    return result;
};

/**
 * Base64 -> UTF-8 string 변환 함수
 * @author bakim
 * @param value UTF-8로 변환할 Base64 데이터
 * @returns UTF-8 string
 */
const decodeUnicode = (value: string) => {
    return Buffer.from(value, "base64").toString("utf-8");
};

/**
 * UTF-8 -> Base64 string 변환 함수
 * @author bakim
 * @param value Base64로 변환할 UTF-8 데이터
 * @returns Base64 string
 */
const encodeUnicode = (value: string) => {
    return Buffer.from(value, "utf8").toString("base64");
    // return Buffer.from(value).toString("base64");
};

const isDev = () => {
    // eslint-disable-next-line no-restricted-globals
    const hostname = location.hostname.toLowerCase().replace("www.", "");
    return !BASE_URL.includes(hostname) || hostname.includes("30.30.30.") || hostname.includes("localhost");
};

// const findCategoryOption = (list: CategoryOptions[], name: string) => {
//     return list.filter((item) => item.label === name)[0];
// };

const findCategoryOption = (list: CategoryOptions[], name: string) => {
    const normalizedName = name?.trim().normalize();
    return list.find((item) => item.label?.trim().normalize() === normalizedName);
};

/**
 * 이미지 파일 확인(정규식 이용)
 */
const isImageFile = (src: string): boolean => {
    return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(src);
};

export {
    IMP_CODE,
    IMAGEKEY,
    GET_FEATURE_UNIT,
    Color,
    iconSize,
    iconColor,
    path,
    subPaths,
    markerColors,
    GRADE,
    type ListViewSchema,
    type MapUserActivity,
    type MapCount,
    type MapItem,
    type MapInfo,
    type FamousMarker,
    type MenusItem,
    type UserInfo,
    type Membership,
    type POIInfo,
    type Story,
    type PlacePropsList,
    type PlaceProps,
    type PlaceCoords,
    type PlaceCoordsList,
    type ImageField,
    type Field,
    type PrimaryInfo,
    type PlaceInfoSet,
    type AllPlaceData,
    type PlaceData,
    type ImageInfo,
    type MetaData,
    type Metadata,
    type MainDataStyle,
    type Category,
    type CategoryOptions,
    type ButtonOptions,
    type GradeType,
    type PaymentData,
    validateFullEmail,
    validateEmailId,
    validateDomain,
    validatePassword,
    validateName,
    getFormattedNumberStr,
    downloadCSV,
    JSONArrayToCSV,
    findCategoryOption,
    decodeUnicode,
    encodeUnicode,
    isDev,
    isImageFile,
};
